import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import {
    Material,
    ProductPreviewComponent,
    ShoppingcartService,
    TranslationPipe
} from '@prekog/common-ui';
import { Observable } from 'rxjs';
import { HomeConfig } from '../shared/home-config.model';
import { FindHomeConfigRequest } from '../shared/find-home-config.request';
import { GetPackageProductsRequest } from '../shared/get-package-products.request';
import { GetPackageProductsResponse } from '../shared/get-package-products.response';
import { SiteConfiguration } from '../shared/site-configuration.model';
import { Package } from '../shared/package.model';
import { GetPackageProductsUseCase } from '../use-cases/get-package-products.usecase';
import { DiscountedProductsWithPackagesResponse } from '../shared/discounted-products-with-packages.response';
import { GetDiscountedProductsWithPackagesUseCase } from '../use-cases/getdiscountedproductswithpackages/get-discounted-product-with-packages.usecase';
import { GetSiteConfigurationByKeyUseCase } from '../use-cases/getsiteconfigurationbykey/get-site-configuration-by-key.usecase';
import { OfferHeaderComponent } from '../shared/components/offerheader/offer-header.component';
import { GetHomeConfigNameUseCase } from '../use-cases/get-home-config-name.usecase';

@Component({
    selector: 'pf-discounted-package-offering',
    templateUrl: './discounted-package-offering.component.html',
    styleUrls: ['./discounted-package-offering.component.scss'],
    standalone: true,
    imports: [CommonModule, MatIconModule, TranslationPipe, RouterModule, ProductPreviewComponent, OfferHeaderComponent]
})
export class DiscountedPackageOfferingComponent implements OnInit {
    homeConfig: HomeConfig = {};
    offerLines = signal<string[]>([]);
    offerLinesActive = signal<boolean>(true);
    packages: Material[] = [];
    router = inject(Router);

    private _packageObjects: Package[] = [];
    private _products: Material[] = [];
    private readonly _discountedProductsWithPackagesUseCase = inject(GetDiscountedProductsWithPackagesUseCase);
    private readonly _getHomeConfigNameUseCase = inject(GetHomeConfigNameUseCase);
    private readonly _getOfferLinesUseCase = inject(GetSiteConfigurationByKeyUseCase);
    private readonly _getPackageProductsUseCase = inject(GetPackageProductsUseCase);
    private readonly _shoppingcartService = inject(ShoppingcartService);

    ngOnInit(): void {
        (this._discountedProductsWithPackagesUseCase.execute({}) as Observable<DiscountedProductsWithPackagesResponse>).subscribe(
            (response) => {
                this._packageObjects = (response.packageList as Package[]) || [];
                this.packages = this._packageObjects.map((pkg) => this._mapPackageToMaterial(pkg));

                this._packageObjects.forEach((pkg, index) => {
                    if (!pkg.packagePrice && !pkg.originalPrice) {
                        this._fetchPackagePrices(pkg, index);
                    }
                });

                this._loadHomeConfigName();
                this._getOfferLines();
            }
        );
    }

    private _fetchPackagePrices(pkg: Package, index: number): void {
        const request: GetPackageProductsRequest = { routerLink: pkg.routerLink };
        (this._getPackageProductsUseCase.execute(request) as Observable<GetPackageProductsResponse>).subscribe(
            (response) => {
                const products = response.products as Material[];
                if (products && products.length > 0) {
                    let packagePrice = 0;
                    let originalPrice = 0;
                    products.forEach((product) => {
                        packagePrice += product.packagePrice || 0;
                        originalPrice += product.originalPrice || 0;
                    });
                    this.packages[index].oldPrice = originalPrice;
                    this.packages[index].originalPrice = packagePrice;
                    this.packages[index].discountPriceActive = true;
                }
            }
        );
    }

    private _mapPackageToMaterial(pkg: Package): Material {
        const material: Material = {};
        material.title = pkg.packageName;
        material.url = pkg.imageUrl;
        material.oldPrice = pkg.originalPrice;
        material.originalPrice = pkg.packagePrice;
        material.discountPriceActive = true;
        material.typeOfOriginalPrice = 'Fogyasztói ár:';
        material.packageName = pkg.packageName;
        material.routerLink = pkg.routerLink;
        return material;
    }

    private _loadHomeConfigName(): void {
        const findHomeConfigRequest: FindHomeConfigRequest = {
            component: 'pf-discounted-packages'
        };
        (this._getHomeConfigNameUseCase.execute(findHomeConfigRequest) as Observable<HomeConfig>).subscribe(
            (homeConfig) => {
                this.homeConfig = homeConfig;
            }
        );
    }

    private _getOfferLines(): void {
        (this._getOfferLinesUseCase.execute({ configurationKey: 'pf-discounted-packages-lines' }) as Observable<SiteConfiguration>).subscribe(
            (config) => {
                this.offerLinesActive.set(config.active ?? true);
                this.offerLines.set(config.configurationValue ? config.configurationValue.split('\n') : []);
            }
        );
    }

    addToCartPackage(material: Material): void {
        const packageProductRequest: GetPackageProductsRequest = {
            routerLink: material.routerLink
        };

        (this._getPackageProductsUseCase.execute(packageProductRequest) as Observable<GetPackageProductsResponse>).subscribe(
            (packageProductResponse) => {
                const response = packageProductResponse;
                const packageObject = response.packageObject as Package;
                this._products = response.products as Material[];

                if (!this._products || this._products.length === 0) {
                    return;
                }

                this._initializePackage(packageObject);
                this._shoppingcartService.addToCartPackage(this._products, packageObject);
            }
        );
    }

    private _initializePackage(packageObject: Package): void {
        this._products.forEach((value) => {
            value.oldPrice = value.originalPrice;
            value.originalPrice = value.packagePrice;
            value.packageName = packageObject.packageName;
        });
        this._products[0].firstElementOfPackage = true;
    }
}
